import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import classnames from 'classnames';

import DatePickerField from '../fields/DatePickerField';
import TextField from '../fields/TextField';
import DropdownField from '../fields/DropdownField';

import ValidatableField from './ValidatableField';
import { flattenTimeZones, toUI } from '../../utility/mapStaticData';
import icons from '../../assets/icons';

import './DateTimeField.css';

const formatDate = (date) => {
  if (!date || !date.Date) {
    return {
      hour: null,
      minutes: null,
      seconds: null
    };
  }
  const dateString = date.Time
    ? [date.Date, date.Time].join('T')
    : date.Date;

  const dateObj = moment(dateString);
  return (
    {
      date: dateObj.format('DD/MM/YYYY'),
      hour: dateObj.format('HH'),
      minutes: dateObj.format('mm'),
      seconds: dateObj.format('ss')
    }
  );
};

const convertToTimeString = (hour, minutes, seconds) => {
  const timeValues = [hour, minutes, seconds];
  const formattedTimeValues = timeValues.map((val) => {
    const timeParam = val || '';
    return timeParam.toString().padStart(2, '0');
  });
  const timeString = formattedTimeValues.join(':');

  return timeString;
};

const dateEquals = (date1, date2) => {
  if (!date1 && !date2) {
    return true;
  }
  if (date1 && date2) {
    return date1.Date === date2.Date && date1.Time === date2.Time;
  }
  return false;
};

class DateTimeFieldComponent extends ValidatableField {
  constructor(props) {
    super(props);
    this.state.value = props.value;
    this.state.formattedValue = formatDate(props.value);
  }

  static get propTypes() {
    return {
      label: PropTypes.string,
      value: PropTypes.any,
      seconds: PropTypes.bool,
      time: PropTypes.bool,
      editing: PropTypes.bool,
      clipboardDate: PropTypes.any,
      name: PropTypes.string.isRequired,
      onChange: PropTypes.func.isRequired,
      onCopy: PropTypes.func,
      required: PropTypes.bool,
      timeZones: PropTypes.array,
      onNow: PropTypes.func,
      validationMessage: PropTypes.string
    };
  }

  static get defaultProps() {
    return {
      validationMessage: 'Please enter a valid date and time',
      time: true,
      seconds: true
    };
  }

  componentWillReceiveProps(props) {
    if (!dateEquals(this.props.value, props.value)) {
      this.setState({
        value: props.value,
        formattedValue: formatDate(props.value)
      }, () => this.validate());
    }
  }

  validate() {
    const { value } = this.state;
    const { required } = this.props;
    if ((!value || !value.Date) && !required) {
      return true;
    }

    const date = value || { Date: null, Time: null };
    const dateString = date.Time
      ? [date.Date, date.Time].join('T')
      : date.Date;

    if (!dateString || !moment(dateString).isValid()) {
      this.setValid(false);
      return false;
    }
    this.setValid(true);
    return true;
  }

  currentDate() {
    const todayDate = moment().format('YYYY-MM-DD');

    return todayDate;
  }

  getDateValue() {
    const { value, formattedValue } = this.state;
    const { hour, minutes, seconds } = formattedValue;
    const date = Object.assign({}, value);
    date.Time = convertToTimeString(hour, minutes, seconds);
    return date;
  }

  onChange() {
    const { onChange } = this.props;
    const newDate = this.getDateValue();

    this.setState({ value: newDate }, () => {
      if (this.validate()) {
        onChange(newDate);
      }
    });
  }

  onChangeDate(updatedDate) {
    const { onChange } = this.props;
    let newDate = Object.assign({}, this.state.value);
    newDate.Date = updatedDate;
    // If no date string, set the whole date object to null
    // and change if valid (for case of clearing optional date)
    if (!updatedDate) {
      newDate = null;
      this.setState({ value: newDate, formattedValue: formatDate(newDate) }, () => {
        if (this.validate()) {
          onChange(newDate);
        }
      });
    } else {
      // If date string, validate the whole date as normal
      this.setState({ value: newDate }, () => this.onChange());
    }
  }

  onChangeTime(key, value) {
    const formattedDate = Object.assign({}, this.state.formattedValue);

    formattedDate[key] = value.toString().padStart(2, '0');
    this.setState({ formattedValue: formattedDate }, () => this.onChange());
  }

  onChangeHours(updatedHours) {
    this.onChangeTime('hour', updatedHours);
  }

  onChangeMinutes(updatedMinutes) {
    this.onChangeTime('minutes', updatedMinutes);
  }

  onChangeSeconds(updatedSeconds) {
    this.onChangeTime('seconds', updatedSeconds);
  }

  onChangeTimeZone(updatedZone) {
    const newDate = Object.assign({}, this.state.value);
    newDate.TimeZoneName = updatedZone ? updatedZone.ZoneName : null;
    newDate.UTCOffset = updatedZone ? updatedZone.UTCOffset : null;

    this.setState({ value: newDate }, () => this.onChange());
  }

  onCopy(event) {
    event.preventDefault();

    const { onCopy } = this.props;
    if (onCopy) onCopy(this.getDateValue());
  }

  onPaste(event) {
    event.preventDefault();

    const { clipboardDate } = this.props;
    if (clipboardDate) {
      this.setState({
        value: clipboardDate,
        formattedValue: formatDate(clipboardDate)
      }, () => this.validate());
    }
  }

  onNow(event) {
    event.preventDefault();
    const { onSetCurrentTime } = this.props;
    onSetCurrentTime(this.state.value ? this.state.value.TimeZoneName : null);
  }
  render() {
    const { label, name, editing, timeZones, required, onCopy, clipboardDate } = this.props;
    const value = this.state.value;
    const possibleValues = flattenTimeZones(timeZones);
    const defaultDisplayName = value ? value.DisplayName : null;
    const valueTimezone = value ? possibleValues.filter(p => p.ZoneName === value.TimeZoneName) : null;
    const valueDisplayName = valueTimezone ? valueTimezone.DisplayName : defaultDisplayName;
    const defaultTimeZone = value ? {
      DisplayName: valueDisplayName,
      ZoneName: value.TimeZoneName,
      UTCOffset: value.UTCOffset
    } : null;
    // Set default time zone here to user location

    const formattedDate = this.state.formattedValue;
    return (
      <div className="DateTimeField">
        <div className="FieldsContainer-group">
          <DatePickerField required={required} className="datepicker" name={`${name}-date`} onChange={updatedValue => this.onChangeDate(updatedValue)} label={label} editing={editing} value={value} validationMessage={null} />
          {this.props.time &&
            <TextField required className="digit" name={`${name}-hours`} onChange={updatedValue => this.onChangeHours(updatedValue)} label="h" value={formattedDate.hour} editing={editing} validationMessage={null} />
          }
          {this.props.time &&
            <TextField required className="digit" name={`${name}-minutes`} onChange={updatedValue => this.onChangeMinutes(updatedValue)} label="m" value={formattedDate.minutes} editing={editing} validationMessage={null} />
          }
          {(this.props.time && this.props.seconds) &&
            <TextField required className="digit" name={`${name}-seconds`} onChange={updatedValue => this.onChangeSeconds(updatedValue)} label="s" value={formattedDate.seconds} editing={editing} validationMessage={null} />
          }
          {this.props.time &&
            <DropdownField
              name={`${name}-time-zone`}
              onChange={updatedValue => this.onChangeTimeZone(updatedValue)}
              label="Time Zone"
              value={defaultTimeZone}
              required
              editing={editing}
              possibleValues={possibleValues}
              mapFn={toUI.timeZone}
              className="timeZone"
              validationMessage={null}
            />
          }
          {editing &&
          <div className="buttonContainer">
            <div className="buttonsCopyPaste">
              <button
                className={classnames('btn', 'btn-inverse')}
                onClick={event => this.onNow(event)}
                title="Now"
              >
                <img alt="Now" src={icons.grey.clock16} />
              </button>
              <button
                className={classnames('btn', 'btn-inverse', { hidden: !onCopy || !value })}
                onClick={event => this.onCopy(event)}
                title="Copy"
              >
                <img alt="Copy" src={icons.grey.copy16} />
              </button>
              <button
                className={classnames('btn', 'btn-inverse', { hidden: !clipboardDate })}
                onClick={event => this.onPaste(event)}
                title="Paste"
              >
                <img alt="Paste" src={icons.grey.paste16} />
              </button>
            </div>
          </div>
          }
        </div>
        {this.renderMessage()}
      </div>
    );
  }
}

export default DateTimeFieldComponent;



// WEBPACK FOOTER //
// ./src/recordDetail/fields/DateTimeField.js